import React, { useState } from 'react';
import IntakeFormLayout from '../components/intake-form/IntakeFormLayout';
import ClientInformation from '../components/intake-form/steps/ClientInformation';
import SpouseAndFamily from '../components/intake-form/steps/SpouseAndFamily';
import ExistingEstateDocuments from '../components/intake-form/steps/ExistingEstateDocuments';
import Beneficiaries from '../components/intake-form/steps/Beneficiaries';
import SpecialNeeds from '../components/intake-form/steps/SpecialNeeds';
import AssetsAndInsurance from '../components/intake-form/steps/AssetsAndInsurance';
import FinancialPOA from '../components/intake-form/steps/FinancialPOA';
import HealthcarePOA from '../components/intake-form/steps/HealthcarePOA';
import FormSummary from '../components/intake-form/FormSummary';
import ConfirmationPage from '../components/intake-form/ConfirmationPage';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { generateFormHtml } from '../utils/generateFormHtml';
// Define the form schema using Zod
const formSchema = z.object({
  clientInfo: z.object({
    existingClient: z.boolean().optional(),
    referralSource: z.string().optional(),
    fullName: z.string().min(1, 'Full name is required'),
    age: z.string().min(1, 'Age is required'),
    maritalStatus: z.string().min(1, 'Marital status is required'),
    address: z.string().min(1, 'Address is required'),
    phone: z.string().min(1, 'Phone number is required'),
    email: z.string().email('Invalid email address'),
    occupation: z.string().optional(),
    employerName: z.string().optional(),
    businessAddress: z.string().optional(),
    businessPhone: z.string().optional(),
    citizenship: z.string().optional()
  })
});
type FormData = z.infer<typeof formSchema>;
const IntakeForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      clientInfo: {
        existingClient: false,
        referralSource: '',
        fullName: '',
        age: '',
        maritalStatus: '',
        address: '',
        phone: '',
        email: '',
        occupation: '',
        employerName: '',
        businessAddress: '',
        businessPhone: '',
        citizenship: ''
      }
    },
    mode: 'onChange'
  });
  const steps = [{
    id: 'client-info',
    title: 'Client Information',
    component: <ClientInformation data-id="element-1070" />
  }, {
    id: 'spouse-family',
    title: 'Spouse & Family',
    component: <SpouseAndFamily data-id="element-1071" />
  }, {
    id: 'estate-docs',
    title: 'Existing Estate Documents',
    component: <ExistingEstateDocuments data-id="element-1072" />
  }, {
    id: 'beneficiaries',
    title: 'Beneficiaries',
    component: <Beneficiaries data-id="element-1073" />
  }, {
    id: 'special-needs',
    title: 'Corporate, Dependents & Special Needs',
    component: <SpecialNeeds data-id="element-1074" />
  }, {
    id: 'assets-insurance',
    title: 'Assets & Insurance',
    component: <AssetsAndInsurance data-id="element-1075" />
  }, {
    id: 'financial-poa',
    title: 'Power of Attorney: Financial',
    component: <FinancialPOA data-id="element-1076" />
  }, {
    id: 'healthcare-poa',
    title: 'Power of Attorney: Healthcare',
    component: <HealthcarePOA data-id="element-1077" />
  }, {
    id: 'summary',
    title: 'Review & Confirm',
    component: <FormSummary onEditStep={stepIndex => setCurrentStep(stepIndex)} data-id="element-1078" />
  }];
  // Define which fields to validate per step
  const stepFields: Record<number, string[]> = {
    0: ['clientInfo.fullName', 'clientInfo.age', 'clientInfo.maritalStatus', 'clientInfo.address', 'clientInfo.phone', 'clientInfo.email']
    // Steps 1-7 have optional fields — no blocking validation needed
  };
  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      const fieldsToValidate = stepFields[currentStep];
      if (fieldsToValidate && fieldsToValidate.length > 0) {
        const isValid = await methods.trigger(fieldsToValidate as any);
        if (!isValid) {
          // Scroll to first error
          const firstErrorField = fieldsToValidate.find(field => {
            const parts = field.split('.');
            let err: any = methods.formState.errors;
            for (const part of parts) {
              err = err?.[part];
            }
            return !!err;
          });
          if (firstErrorField) {
            const fieldName = firstErrorField.split('.').pop();
            const el = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
            if (el) {
              el.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              el.focus();
            }
          }
          return;
        }
      }
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const allFormData = methods.getValues();
      const htmlContent = generateFormHtml(allFormData);
      const response = await fetch('https://n8n.digilabs.ca/webhook/closemyhome', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          html: htmlContent,
          clientName: allFormData.clientInfo?.fullName || '',
          clientEmail: allFormData.clientInfo?.email || '',
          submittedAt: new Date().toISOString()
        })
      });
      if (!response.ok) {
        throw new Error(`Submission failed (${response.status})`);
      }
      console.log('Form submitted successfully');
      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    methods.reset();
    setCurrentStep(0);
    setIsSubmitted(false);
    setSubmitError(null);
    window.scrollTo(0, 0);
  };
  if (isSubmitted) {
    return <ConfirmationPage onReset={handleReset} data-id="element-1079" />;
  }
  return <FormProvider {...methods} data-id="element-1080">
      <IntakeFormLayout currentStep={currentStep} totalSteps={steps.length} title={steps[currentStep].title} onNext={currentStep === steps.length - 1 ? handleSubmit : handleNext} onBack={handleBack} isFirstStep={currentStep === 0} isLastStep={currentStep === steps.length - 1} steps={steps} isSubmitting={isSubmitting} data-id="element-1081">
        {steps[currentStep].component}
        {submitError && currentStep === steps.length - 1 && <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4 flex items-start" data-id="element-1082">
            <svg className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1083">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-1084" />
            </svg>
            <div data-id="element-1085">
              <p className="font-medium text-red-800" data-id="element-1086">Submission Failed</p>
              <p className="text-sm text-red-700 mt-1" data-id="element-1087">{submitError}</p>
              <p className="text-sm text-red-600 mt-1" data-id="element-1088">
                Please try again. If the problem persists, contact our office
                directly.
              </p>
            </div>
          </div>}
      </IntakeFormLayout>
    </FormProvider>;
};
export default IntakeForm;